import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FaCalendar, FaArrowRight } from "react-icons/fa";
import { useLanguage } from "./LanguageProvider";
import { Link } from "react-router-dom";
import Galaxy from "./Galaxy";

export const Blogs = ({ threeblogs = [], bgColor = "var(--premium-bg)", mainColor = "#ae0001" }) => {
  const { currentLang } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const isArabic = currentLang?.toLowerCase() === "ar";
  const direction = isArabic ? "rtl" : "ltr";

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    const cleanUrl = url.replace(/^undefined\//, "");
    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) return cleanUrl;
    if (cleanUrl.startsWith("/")) return `http://localhost:8000${cleanUrl}`;
    return `http://localhost:8000/${cleanUrl}`;
  };

  const stripHtml = (value = "") => {
    if (!value || typeof value !== "string") return "";
    return value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  };

  const truncateText = (text, maxLength = 110) => {
    const cleanText = stripHtml(text);
    if (!cleanText) return "";
    if (cleanText.length <= maxLength) return cleanText;
    return `${cleanText.substring(0, maxLength).trim()}...`;
  };

  const translations = {
    EN: {
      sectionEyebrow: "Our Blogs",
      sectionTitle: ["Latest Articles &", "Guides"],
      sectionSubtitle: "Explore FlyVia articles about Spain residency, Schengen visas, company formation, relocation, and practical tips for moving to Europe.",
      spainResidencyCategory: "Spain Residency",
      readFullArticle: "Read Full Article",
      viewAllArticles: "View All Articles",
      defaultAuthor: "FlyVia Team",
      defaultTitle: "FlyVia Article",
      noBlogs: "No blogs available yet",
    },
    AR: {
      sectionEyebrow: "مقالاتنا",
      sectionTitle: ["أحدث المقالات و", "الدلائل"],
      sectionSubtitle: "استكشف مقالات FlyVia حول الإقامة في إسبانيا، فيزا الشنجن، تأسيس الشركات، الانتقال، والنصائح العملية للانتقال إلى أوروبا.",
      spainResidencyCategory: "الإقامة في إسبانيا",
      readFullArticle: "اقرأ المقال كاملًا",
      viewAllArticles: "عرض جميع المقالات",
      defaultAuthor: "فريق FlyVia",
      defaultTitle: "مقال FlyVia",
      noBlogs: "لا توجد مقالات حاليًا",
    },
  };

  const t = (key) => {
    const langKey = currentLang?.toUpperCase?.() || "EN";
    return translations[langKey]?.[key] || translations.EN[key] || key;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  const getLocalizedData = (blog) => {
    const lang = isArabic ? "ar" : "en";
    return {
      title: blog?.title?.[lang] || blog?.title?.ar || blog?.title?.en || t("defaultTitle"),
      excerpt: blog?.short_description?.[lang] || blog?.short_description?.ar || blog?.short_description?.en || "",
      author: blog?.author || t("defaultAuthor"),
      createdAt: blog?.createdAt ? new Date(blog.createdAt).toLocaleDateString(isArabic ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" }) : "January 1, 2026",
      slug: blog?.slug?.[lang] || blog?.slug?.ar || blog?.slug?.en || blog?._id,
    };
  };

  const visibleBlogs = useMemo(() => {
    if (!Array.isArray(threeblogs)) return [];
    return threeblogs.slice(0, 3);
  }, [threeblogs]);

  const renderBlogs = () => {
    if (visibleBlogs.length > 0) {
      return visibleBlogs.map((blog, index) => {
        const blogId = blog?._id || `blog-${index}`;
        const localizedData = getLocalizedData(blog);
        const blogImageRaw = blog?.profileImg || blog?.image || null;
        const blogImage = blogImageRaw ? getImageUrl(blogImageRaw) : "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80";
        const blogSlug = isArabic ? blog?.slug?.ar : blog?.slug?.en;
        const category = blog?.tags?.[isArabic ? "ar" : "en"]?.[0] || t("spainResidencyCategory");

        return (
          <article key={blogId} className={`flyvia-blog-card ${isVisible ? "is-visible" : ""}`} style={{ direction, transitionDelay: `${index * 150}ms` }}>
            <Link to={`/BlogDetails/${blogId}/${blogSlug || localizedData.slug}`} className="flyvia-blog-image-link">
              <div className="flyvia-blog-image">
                <img 
                  src={blogImage} 
                  alt={localizedData.title} 
                  loading="lazy" 
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = '/Flyvia Logo.png';
                  }}
                />
                <span className="flyvia-blog-category">{category}</span>
              </div>
            </Link>
            <div className="flyvia-blog-content">
              <div className="flyvia-blog-meta"><span>{localizedData.author}</span><span className="dot" /><span>{localizedData.createdAt}</span></div>
              <h3 className="flyvia-blog-title">{localizedData.title}</h3>
              <p className="flyvia-blog-excerpt">{truncateText(localizedData.excerpt, 110)}</p>
              <Link to={`/BlogDetails/${blogId}/${blogSlug || localizedData.slug}`} className="flyvia-read-more">
                {t("readFullArticle")} <FaArrowRight size={10} className={isArabic ? "rtl-arrow" : ""} />
              </Link>
            </div>
          </article>
        );
      });
    }
    return <div className="flyvia-empty-state"><h3>{t("noBlogs")}</h3></div>;
  };

  const isDark = bgColor === "var(--premium-bg)" || bgColor === "#0f1218";

  return (
    <section
      ref={sectionRef}
      id="blog"
      dir={direction}
      className=" relative overflow-hidden"
      style={{ 
          background: 'var(--premium-bg)', 
          marginTop: '-120px', 
          paddingTop: '120px', 
          paddingBottom: '240px',
          clipPath: 'polygon(0% 0%, 100% 5%, 100% 85%, 0% 90%)'
        }}
    >
      <style>{`
        .flyvia-blogs-section { font-family: var(--font-primary); }
        .flyvia-eyebrow { display: block; margin-bottom: 24px; color: ${mainColor}; font-size: 13px; font-weight: 600; letter-spacing: 0.4em; text-transform: uppercase; font-family: var(--font-secondary); }
        .flyvia-blogs-title { margin: 0; color: ${isDark ? "#f5f0e8" : "#1a1a1a"}; font-weight: 300; line-height: 1.1; letter-spacing: -0.01em; font-family: var(--font-heading); opacity: 0; transform: translateY(30px); transition: all 1s ease; }
        .flyvia-blogs-title.visible { opacity: 1; transform: translateY(0); }
        .flyvia-blogs-title em { font-style: italic; color: ${mainColor}; font-family: 'Cormorant Garamond', serif; }
        [dir="rtl"] .flyvia-blogs-title em { font-family: inherit; }
        
        .flyvia-blogs-subtitle { max-width: 480px; margin: 32px 0 0; color: ${isDark ? "rgba(255,255,255,0.4)" : "#666"}; font-size: 14px; font-weight: 300; line-height: 1.8; font-family: var(--font-secondary); opacity: 0; transform: translateY(30px); transition: all 1s ease 100ms; }
        .flyvia-blogs-subtitle.visible { opacity: 1; transform: translateY(0); }
        .flyvia-blogs-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 40px; margin-top: 80px; }
        .flyvia-blog-card { background: ${isDark ? "rgba(255,255,255,0.03)" : "#ffffff"}; border: 1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"}; border-radius: 24px; overflow: hidden; opacity: 0; transform: translateY(40px); transition: all 1s ease; }
        .flyvia-blog-card.is-visible { opacity: 1; transform: translateY(0); }
        .flyvia-blog-card:hover { transform: translateY(-10px); box-shadow: 0 40px 80px rgba(0,0,0,0.3); }
        .flyvia-blog-image { height: 220px; overflow: hidden; position: relative; }
        .flyvia-blog-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 1.5s ease; filter: brightness(0.9); }
        .flyvia-blog-card:hover .flyvia-blog-image img { transform: scale(1.1); }
        .flyvia-blog-content { padding: 32px; }
        .flyvia-blog-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-family: var(--font-secondary); font-size: 10px; color: ${isDark ? "rgba(255,255,255,0.3)" : "#999"}; text-transform: uppercase; letter-spacing: 0.1em; }
        .flyvia-blog-meta .dot { width: 3px; height: 3px; border-radius: 50%; background: ${mainColor}; opacity: 0.3; }
        .flyvia-blog-title { font-family: var(--font-heading); font-size: 20px; color: ${isDark ? "#f5f0e8" : "#1a1a1a"}; margin-bottom: 12px; line-height: 1.3; font-weight: 300; }
        .flyvia-blog-excerpt { color: ${isDark ? "rgba(255,255,255,0.4)" : "#666"}; font-size: 13px; line-height: 1.7; margin-bottom: 20px; font-family: var(--font-secondary); font-weight: 300; }
        .flyvia-read-more { display: inline-flex; align-items: center; gap: 6px; color: ${mainColor}; font-weight: 600; text-decoration: none !important; font-family: var(--font-secondary); font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; }
        .flyvia-bg-text { position: absolute; bottom: 60px; left: -5%; font-family: var(--font-heading); font-size: 25vw; font-weight: 900; color: rgba(255,255,255,0.015); pointer-events: none; line-height: 0.8; z-index: 0; }
        @media (max-width: 991px) { .flyvia-blogs-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 768px) { .flyvia-blogs-grid { grid-template-columns: 1fr; } .flyvia-blogs-section { padding: 16px 0; } }
      `}</style>

      <div className="flyvia-bg-text">BLOGS</div>

      <div className="max-w-screen-xl mx-auto px-8 relative z-10">
        <Row className="justify-content-center text-center">
          <Col lg={10}>
            <span className="flyvia-eyebrow !text-lg">{t("sectionEyebrow")}</span>
            <h2 className={`flyvia-blogs-title ${isVisible ? "visible" : ""}`} style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>{t("sectionTitle")[0]} <em>{t("sectionTitle")[1]}</em></h2>
            <p className={`flyvia-blogs-subtitle ${isVisible ? "visible" : ""}`} style={{ margin: '32px auto 0' }}>{t("sectionSubtitle")}</p>
          </Col>
        </Row>
        <div className="flyvia-blogs-grid">{renderBlogs()}</div>
        <div className="mt-20 text-center">
          <Link to="/MainBlogsCardspage" style={{ textDecoration: 'none', color: isDark ? 'rgba(255,255,255,0.4)' : '#888', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', fontFamily: 'var(--font-secondary)' }}>
            <span style={{ borderBottom: '1px solid currentColor', paddingBottom: '4px' }}>{t("viewAllArticles")}</span>
          </Link>
        </div>
      </div>
      
      <div 
        style={{
          backgroundImage: "url('/145.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.4,
          backgroundAttachment: 'fixed'
        }}
        className="absolute inset-0 w-full h-full pointer-events-none"
      ></div>
      <Galaxy />
    </section>
  );
};